import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Column,
    FloatingLabel,
    FormField,
    FormInputGroup,
    GridContainer,
    InputWithFloatingLabel,
    Row,
    StandardForm,
    TextInput,
    TextInputWithButtonBeside,
} from '@vp/swan';
import { useUserContext } from '@vp/ubik-context';
import { useTrack } from '../../utils/analytics';
import { removePathQueryParams } from '../../utils/removePathQueryParams';
import FormErrorWrapper from '../FormErrorWrapper/FormErrorWrapper';

interface BusinessNameInputProps {
    onSubmit: (businessName: string) => void;
}

const BusinessNameInput = ({ onSubmit }: BusinessNameInputProps) => {
    const { t } = useTranslation();
    const [formErrorMessage, setFormErrorMessage] = useState('');
    const [inputValue, setInputValue] = useState('');
    const track = useTrack();
    const textInputRef = useRef<HTMLInputElement>(null);
    const maxCharLimit = 50;
    const pathName = useUserContext().forwardedPath || '';
    const currentPagePath = removePathQueryParams(pathName);

    function trackBusinessNameSubmission() {
        track('Business Name Submitted On Logomaker Anywhere', {
            pageSection: 'LogomakerAnywhere',
            category: 'LogomakerAnywhere',
            label: 'Content:LogomakerAnywhere',
            eventDetail: `${currentPagePath};${currentPagePath};LogomakerAnywhere;createLogoFromBusinessName`,
            navigationDetail: 'createLogoFromBusinessName',
            businessName: inputValue,
        });
    }

    const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (inputValue.trim().length === 0) {
            setFormErrorMessage(
                t(
                    'Vista Logos > Logomaker Anywhere > Text Input > Error Message Missing Name.value',
                ),
            );

            // Move focus to the invalid input and interrupt screen reader if button hit mid-announcement.
            textInputRef.current?.focus();

            return;
        }
        if (formErrorMessage) {
            textInputRef.current?.focus();
            return;
        }
        trackBusinessNameSubmission();
        onSubmit(inputValue);
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value.length > maxCharLimit) {
            setFormErrorMessage(
                t(
                    'Vista Logos > Logomaker Anywhere > Text Input > Error Message Max Char Limit.value',
                ),
            );
        } else {
            setFormErrorMessage('');
            setInputValue(value);
        }
    };

    return (
        <GridContainer paddingBottom={6}>
            <Row>
                <Column span={12} spanMd={6} spanLg={4} spanXl={3} paddingX={0}>
                    <StandardForm onSubmit={handleOnSubmit}>
                        <FormField>
                            <FormInputGroup margin={0}>
                                <TextInputWithButtonBeside>
                                    <InputWithFloatingLabel>
                                        <TextInput
                                            value={inputValue}
                                            onChange={handleOnChange}
                                            aria-required
                                            placeholder={t(
                                                'Vista Logos > Logomaker Anywhere > Text Input > Placeholder.value',
                                            )}
                                            aria-invalid={!!formErrorMessage}
                                            aria-errormessage={
                                                formErrorMessage ? 'business-name-error' : undefined
                                            }
                                            name="businessName"
                                            ref={textInputRef}
                                        />
                                        <FloatingLabel>
                                            {t(
                                                'Vista Logos > Logomaker Anywhere > Text Input > Label.value',
                                            )}
                                            <span aria-hidden>*</span>
                                        </FloatingLabel>
                                    </InputWithFloatingLabel>
                                    <Button skin="secondary" type="submit">
                                        {t(
                                            'Vista Logos > Logomaker Anywhere > Text Input > Submit Button.value',
                                        )}
                                    </Button>
                                </TextInputWithButtonBeside>
                                <FormErrorWrapper
                                    errorMessage={formErrorMessage}
                                    errorId="business-name-error"
                                />
                            </FormInputGroup>
                        </FormField>
                    </StandardForm>
                </Column>
            </Row>
        </GridContainer>
    );
};

export default BusinessNameInput;
